import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useSnackbar } from 'notistack';
import VendedorSelect from '../../components/VendedorSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { defaultMesActualRango, useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import comisionesService, {
  ComisionLiquidacionListItem,
  getComisionErrorMessage,
} from '../../services/comisionesService';
import { formatCurrency } from '../../utils/formatters';
import LiquidacionComisionDetalleDialog from './components/LiquidacionComisionDetalleDialog';
import LiquidacionesComisionesResumen from './components/LiquidacionesComisionesResumen';
import { formatFechaComision } from './liquidacionComisionesFormat';

const API_PAGE_SIZE = 20;
const API_MAX_PAGES = 500;

const filtroInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fafafa',
    '&:hover': { bgcolor: '#f5f5f5' },
    '&.Mui-focused': { bgcolor: '#ffffff' },
  },
};

const accionVerSx = {
  bgcolor: '#2196f3',
  color: '#fff',
  borderRadius: 1.5,
  width: 32,
  height: 32,
  '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' },
  transition: 'all 0.2s',
};

const accionFacturaSx = {
  bgcolor: '#2e7d32',
  color: '#fff',
  borderRadius: 1.5,
  width: 32,
  height: 32,
  '&:hover': { bgcolor: '#1b5e20', transform: 'scale(1.1)' },
  transition: 'all 0.2s',
};

const comprobanteSx = {
  cursor: 'pointer',
  color: 'primary.main',
  fontWeight: 500,
  '&:hover': { textDecoration: 'underline' },
};

const parseImporteFiltro = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const displayComprobante = (row: ComisionLiquidacionListItem) =>
  row.formularioDisplay?.trim() || `#${row.nroFormulario}`;

const displayVendedor = (row: ComisionLiquidacionListItem) =>
  row.nombreVendedor?.trim() || (row.nroVendedor != null ? `#${row.nroVendedor}` : '—');

const displayProveedor = (row: ComisionLiquidacionListItem) =>
  row.nombreProveedor?.trim() || (row.nroProveedor != null ? `#${row.nroProveedor}` : '—');

const LiquidacionesComisiones: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } =
    useSucursalesVentaFiltro();

  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importeDesde, setImporteDesde] = useState('');
  const [importeHasta, setImporteHasta] = useState('');
  const [allItems, setAllItems] = useState<ComisionLiquidacionListItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [detalleNro, setDetalleNro] = useState<number | null>(null);

  useEffect(() => {
    if (!filtroListo) return;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        const acumulado: ComisionLiquidacionListItem[] = [];
        let apiPage = 1;
        let totalApi = 0;
        do {
          const result = await comisionesService.getLiquidaciones({
            desde: fechaDesde || undefined,
            hasta: fechaHasta || undefined,
            nroSuc: resolveNroSucFiltro(filtroNroSuc),
            nroVendedor: nroVendedor && nroVendedor > 0 ? nroVendedor : undefined,
            pageNumber: apiPage,
            pageSize: API_PAGE_SIZE,
          });
          if (cancelled) return;
          acumulado.push(...result.items);
          totalApi = result.totalCount;
          if (result.items.length < API_PAGE_SIZE) break;
          apiPage += 1;
        } while (acumulado.length < totalApi && apiPage <= API_MAX_PAGES);
        if (cancelled) return;
        setAllItems(acumulado);
      } catch (error) {
        if (cancelled) return;
        setAllItems([]);
        enqueueSnackbar(getComisionErrorMessage(error, 'Error al cargar las liquidaciones de comisiones'), {
          variant: 'error',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [fechaDesde, fechaHasta, filtroNroSuc, nroVendedor, filtroListo, enqueueSnackbar]);

  const itemsFiltrados = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const min = parseImporteFiltro(importeDesde);
    const max = parseImporteFiltro(importeHasta);
    return allItems.filter((row) => {
      const total = Number(row.total) || 0;
      if (min != null && total < min) return false;
      if (max != null && total > max) return false;
      if (!term) return true;
      return [
        displayComprobante(row),
        String(row.nroFormulario),
        displayVendedor(row),
        displayProveedor(row),
        row.descripcion ?? '',
      ].some((campo) => campo.toLowerCase().includes(term));
    });
  }, [allItems, searchTerm, importeDesde, importeHasta]);

  const resumen = useMemo(() => {
    const totalLiquidado = itemsFiltrados.reduce((acc, r) => acc + (Number(r.total) || 0), 0);
    const vendedores = new Set(
      itemsFiltrados.map((r) => r.nroVendedor).filter((n): n is number => n != null && n > 0),
    );
    const proveedores = new Set(
      itemsFiltrados.map((r) => r.nroProveedor).filter((n): n is number => n != null && n > 0),
    );
    return {
      cantidad: itemsFiltrados.length,
      totalLiquidado,
      promedio: itemsFiltrados.length > 0 ? totalLiquidado / itemsFiltrados.length : 0,
      vendedores: vendedores.size,
      proveedores: proveedores.size,
    };
  }, [itemsFiltrados]);

  const pageItems = useMemo(
    () => itemsFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [itemsFiltrados, page, rowsPerPage],
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(itemsFiltrados.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [itemsFiltrados.length, rowsPerPage, page]);

  const rangoDefault = defaultMesActualRango();
  const hayFiltrosExtra =
    searchTerm.trim() !== '' ||
    importeDesde !== '' ||
    importeHasta !== '' ||
    (nroVendedor ?? 0) > 0 ||
    fechaDesde !== rangoDefault.fechaDesde ||
    fechaHasta !== rangoDefault.fechaHasta;

  const handleLimpiarFiltros = () => {
    setSearchTerm('');
    setImporteDesde('');
    setImporteHasta('');
    setNroVendedor(null);
    setFechaDesde(rangoDefault.fechaDesde);
    setFechaHasta(rangoDefault.fechaHasta);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVerFactura = (nroFormulario: number) => {
    navigate(`/compras/facturas/${nroFormulario}`);
  };

  const renderAcciones = (row: ComisionLiquidacionListItem) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      <Tooltip title="Ver detalle">
        <IconButton size="small" onClick={() => setDetalleNro(row.nroFormulario)} sx={accionVerSx}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ver Factura X">
        <IconButton size="small" onClick={() => handleVerFactura(row.nroFormulario)} sx={accionFacturaSx}>
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const pagination = (
    <TablePagination
      rowsPerPageOptions={[5, 10, 25]}
      component="div"
      count={itemsFiltrados.length}
      rowsPerPage={rowsPerPage}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={handleChangeRowsPerPage}
      labelRowsPerPage="Filas por página"
      sx={{ borderTop: '1px solid #e0e0e0', '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 } }}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              Liquidaciones de comisiones
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterAltOffIcon />}
                onClick={handleLimpiarFiltros}
                disabled={!hayFiltrosExtra}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 500 }}
              >
                Limpiar filtros
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/ventas/liquidacion-comisiones/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
                }}
              >
                Nueva liquidación
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por comprobante, nro. interno, vendedor, proveedor o descripción..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={filtroInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={filtroInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={filtroInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => {
                    setFiltroNroSuc(v);
                    setPage(0);
                  }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <VendedorSelect
                  label="Vendedor"
                  value={nroVendedor}
                  onChange={(nro) => {
                    setNroVendedor(nro);
                    setPage(0);
                  }}
                  allowEmpty
                  emptyOptionLabel="Todos"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Importe desde"
                  type="number"
                  value={importeDesde}
                  onChange={(e) => {
                    setImporteDesde(e.target.value);
                    setPage(0);
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={filtroInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Importe hasta"
                  type="number"
                  value={importeHasta}
                  onChange={(e) => {
                    setImporteHasta(e.target.value);
                    setPage(0);
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={filtroInputSx}
                />
              </Grid>
            </Grid>
          </Box>

          <LiquidacionesComisionesResumen
            cantidad={resumen.cantidad}
            totalLiquidado={resumen.totalLiquidado}
            promedio={resumen.promedio}
            vendedores={resumen.vendedores}
            proveedores={resumen.proveedores}
          />

          {isMobile ? (
            <Stack spacing={2}>
              {pageItems.map((row) => (
                <Card
                  key={row.nroFormulario}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography
                        component="span"
                        variant="subtitle1"
                        sx={{ ...comprobanteSx, fontWeight: 600, display: 'block' }}
                        onClick={() => setDetalleNro(row.nroFormulario)}
                      >
                        {displayComprobante(row)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Fecha:</strong> {formatFechaComision(row.fechaEmision)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Vendedor:</strong> {displayVendedor(row)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Proveedor:</strong> {displayProveedor(row)}
                      </Typography>
                      {row.descripcion?.trim() && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Descripción:</strong> {row.descripcion.trim()}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        <strong>Total:</strong> {formatCurrency(row.total ?? 0)}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex' }}>{renderAcciones(row)}</Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {!loading && pageItems.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  Sin liquidaciones para el filtro indicado.
                </Typography>
              )}
              {pagination}
            </Stack>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0', overflowX: 'auto' }}
            >
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160, whiteSpace: 'nowrap' }}>
                      Comprobante
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vendedor</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' } }}>
                      Descripción
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center" width={120}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : pageItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">Sin liquidaciones para el filtro indicado.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageItems.map((row) => (
                      <TableRow key={row.nroFormulario} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell>{formatFechaComision(row.fechaEmision)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography
                            component="span"
                            sx={comprobanteSx}
                            onClick={() => setDetalleNro(row.nroFormulario)}
                          >
                            {displayComprobante(row)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{displayVendedor(row)}</TableCell>
                        <TableCell>{displayProveedor(row)}</TableCell>
                        <TableCell
                          sx={{
                            display: { xs: 'none', lg: 'table-cell' },
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.descripcion?.trim() || '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {formatCurrency(row.total ?? 0)}
                        </TableCell>
                        <TableCell align="center">{renderAcciones(row)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {pagination}
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <LiquidacionComisionDetalleDialog nroFormulario={detalleNro} onClose={() => setDetalleNro(null)} />
    </Box>
  );
};

export default LiquidacionesComisiones;
